import React, { useCallback, useEffect, useRef, useState } from "react";
import { useReveal } from "../hooks/useReveal";

const TESTIMONIALS_COPY = {
  pt: {
    eyebrow: "Testemunhos dos nossos",
    title: "Clientes",
    prev: "Anterior",
    next: "Seguinte",
    goToSlide: (idx) => `Ir para o slide ${idx}`,
    regionLabel: "Testemunhos",
    items: [
      {
        quote:
          "Embora o serviço seja de excelência, o que mais se destaca nesta empresa é o fator humano. Se, por algum motivo, há necessidade de uma resposta urgente, esta surge imediatamente. Há uma autêntica parceria e empatia, e isso é muito benéfico!",
        name: "Nádia Pimenta",
        role: "Marketing Manager",
        rating: 5,
      },
      {
        quote:
          "Não é todos os dias que nos cruzamos com uma empresa que se apresenta como um potencial cliente e se torna num dos parceiros estratégicos mais relevantes da nossa organização.",
        name: "Sérgio Antunes",
        role: "General Manager",
        rating: 5,
      },
      {
        quote:
          "Fácil, Ágil, BOM! A relação passou de cliente, a fornecedor, de parceiros a amigos 🙂 O serviço é rápido e eficaz, com capacidade de entrega e preço competitivo. Só tenho boas coisas a dizer e é um prazer acompanhar de perto o constante crescimento.",
        name: "Nuno Lopes",
        role: "BDM",
        rating: 5,
      },
    ],
  },
  en: {
    eyebrow: "Testimonials from our",
    title: "Clients",
    prev: "Previous",
    next: "Next",
    goToSlide: (idx) => `Go to slide ${idx}`,
    regionLabel: "Testimonials",
    items: [
      {
        quote:
          "Even though the service is excellent, what stands out the most is the human factor. Whenever we need an urgent response it arrives immediately. It feels like a true partnership with real empathy, and that makes all the difference!",
        name: "Nádia Pimenta",
        role: "Marketing Manager",
        rating: 5,
      },
      {
        quote:
          "It isn’t every day that we meet a company that first appears as a potential client and quickly becomes one of the most strategic partners in our organisation.",
        name: "Sérgio Antunes",
        role: "General Manager",
        rating: 5,
      },
      {
        quote:
          "Easy, Agile, GREAT! The relationship evolved from client to supplier, from partners to friends. Service is fast and effective, with delivery capacity and competitive pricing. Only good things to say — it’s a pleasure to watch their continuous growth up close.",
        name: "Nuno Lopes",
        role: "BDM",
        rating: 5,
      },
    ],
  },
};

export default function TestimonialsSection({ lang = "pt" }) {
  const copy = TESTIMONIALS_COPY[lang] || TESTIMONIALS_COPY.pt;
  const TESTIMONIALS = copy.items;

  const brand = "#4CB1A1";

  const [curr, setCurr] = useState(0);
  const [prev, setPrev] = useState(null); // slide anterior (para md+)
  const [dir, setDir] = useState(1);      // 1 = next, -1 = prev

  const leftColumnRef = useRef(null);
  const carouselRef = useRef(null);
  const dotsRef = useRef(null);

  useReveal(leftColumnRef);
  useReveal(carouselRef, { threshold: 0.15 });
  useReveal(dotsRef, { threshold: 0.15 });

  const goPrev = useCallback(() => {
    setDir(-1);
    setCurr((p) => {
      setPrev(p);
      return p === 0 ? TESTIMONIALS.length - 1 : p - 1;
    });
  }, [TESTIMONIALS.length]);
  const goNext = useCallback(() => {
    setDir(1);
    setCurr((p) => {
      setPrev(p);
      return (p + 1) % TESTIMONIALS.length;
    });
  }, [TESTIMONIALS.length]);

  useEffect(() => {
    if (prev === null) return;
    const t = setTimeout(() => setPrev(null), 430);
    return () => clearTimeout(t);
  }, [prev]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev]);

  useEffect(() => {
    setCurr(0);
    setPrev(null);
  }, [lang]);

  return (
    <section aria-labelledby="testemunhos-title" className="relative w-full testimonials-section">
      {/* keyframes: mobile tem fade-up; md+ mantém slide lateral */}
      <style>{`
        @media (max-width: 767.98px) {
          @keyframes fadeUpIn  { from { opacity:0; transform: translateY(6px) } to { opacity:1; transform: translateY(0) } }
          .slide.entering { animation: fadeUpIn .28s ease-out forwards; }
          .slide.leaving  { display: none !important; } /* não renderizamos o antigo em mobile */
        }
        @media (min-width: 768px) {
          @keyframes slideInRight { from { opacity:0; transform: translateX(12px) } to { opacity:1; transform: translateX(0) } }
          @keyframes slideOutRight{ from { opacity:1; transform: translateX(0) } to { opacity:0; transform: translateX(-12px) } }
          @keyframes slideInLeft  { from { opacity:0; transform: translateX(-12px)} to { opacity:1; transform: translateX(0) } }
          @keyframes slideOutLeft { from { opacity:1; transform: translateX(0) } to { opacity:0; transform: translateX(12px) } }
          .slide.entering.dir-1   { animation: slideInRight .42s ease-out forwards; }
          .slide.leaving.dir-1    { animation: slideOutRight .42s ease-in forwards; }
          .slide.entering.dir--1  { animation: slideInLeft  .42s ease-out forwards; }
          .slide.leaving.dir--1   { animation: slideOutLeft .42s ease-in forwards; }
        }
      `}</style>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-8 items-start">
          {/* Left: título + setas */}
          <div ref={leftColumnRef} className="reveal md:col-span-4">
            <p className="uppercase tracking-[0.24em] text-[10px] sm:text-[11px] testimonials-eyebrow">
              {copy.eyebrow}
            </p>
            <h2 id="testemunhos-title" className="mt-1 text-2xl sm:text-3xl md:text-4xl font-extrabold testimonials-title">
              {copy.title}
            </h2>

            <div className="mt-5 sm:mt-6 lg:mt-8 flex items-center gap-3 sm:gap-4" style={{ "--brand": brand }}>
              <IconBtn label={copy.prev} onClick={goPrev} brand={brand} dir="left" />
              <IconBtn label={copy.next} onClick={goNext} brand={brand} dir="right" />
            </div>
          </div>

          {/* Right: carrossel */}
          <div className="md:col-span-8">
            <div
              ref={carouselRef}
              className="reveal relative min-h-0 md:min-h-[300px]"
              role="region"
              aria-roledescription="carousel"
              aria-label={copy.regionLabel}
            >
              {/* Slide atual */}
              <Slide key={`curr-${curr}`} item={TESTIMONIALS[curr]} entering dir={dir} brand={brand} />

              {/* Slide a sair — só aparece em md+ (em mobile o CSS oculta) */}
              {prev !== null && (
                <Slide key={`prev-${prev}`} item={TESTIMONIALS[prev]} entering={false} dir={dir} brand={brand} leaving />
              )}
            </div>

            {/* Dots (ligeiramente maiores no mobile para toque) */}
            <div ref={dotsRef} className="reveal mt-5 flex items-center justify-center gap-2 sm:gap-3">
              {TESTIMONIALS.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    if (idx === curr) return;
                    setPrev(curr);
                    setDir(idx > curr ? 1 : -1);
                    setCurr(idx);
                  }}
                  aria-label={copy.goToSlide(idx + 1)}
                  className={[
                    "h-2 w-2 sm:h-2.5 sm:w-2.5 md:h-2 md:w-2 rounded-full transition-all duration-200",
                    idx === curr ? "bg-slate-900 scale-100" : "bg-slate-300 scale-95 opacity-80 hover:opacity-100",
                  ].join(" ")}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function IconBtn({ label, onClick, brand, dir = "left" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="group inline-flex h-10 w-10 items-center justify-center rounded-full border-2 border-current text-[color:var(--brand)] transition-colors duration-200 hover:text-white hover:bg-[var(--brand)] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--brand)]"
      style={{ "--brand": brand }}
    >
      {dir === "left" ? (
        <svg width="18" height="18" viewBox="0 0 24 24" className="transition-transform duration-200 group-active:-translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" className="transition-transform duration-200 group-active:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 6l6 6-6 6" />
        </svg>
      )}
    </button>
  );
}

function Slide({ item, entering, leaving, dir, brand }) {
  /* 🔹 MOBILE (default): relative (flow), paddings/typos mais contidos
     🔹 DESKTOP/TABLET (md+): mantém overlay absoluto exatamente como tinhas */
  const base =
    "slide relative md:absolute md:inset-0 bg-slate-50 rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 shadow-sm will-change-transform will-change-opacity";

  const stateCls = [
    entering ? "entering" : "",
    leaving ? "leaving" : "",
    dir === 1 ? "dir-1" : "dir--1",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={`${base} ${stateCls}`}>
      <div className="mb-3 sm:mb-4">
        <svg width="26" height="26" viewBox="0 0 24 24" fill={brand} aria-hidden="true">
          <path d="M7.17 6A4.17 4.17 0 0 0 3 10.17V18h6v-6H6.5A2.5 2.5 0 0 1 9 9.5 3.5 3.5 0 0 0 7.17 6Zm9 0A4.17 4.17 0 0 0 12 10.17V18h6v-6h-2.5A2.5 2.5 0 0 1 18 9.5 3.5 3.5 0 0 0 16.17 6Z" />
        </svg>
      </div>

      <p className="leading-relaxed text-[15px] sm:text-[16px] testimonials-quote">
        {item.quote}
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2">
        <p className="text-base sm:text-lg md:text-xl font-extrabold testimonials-name">{item.name}</p>
        <div className="flex items-center">
          {Array.from({ length: 5 }).map((_, idx) => (
            <svg key={idx} width="18" height="18" viewBox="0 0 24 24" className="mr-1" aria-hidden="true" fill={idx < item.rating ? "#F6C453" : "#E5E7EB"}>
              <path d="M12 .587l3.668 7.431 8.2 1.192-5.934 5.787 1.4 8.168L12 18.896l-7.334 3.869 1.4-8.168L.132 9.21l8.2-1.192z" />
            </svg>
          ))}
        </div>
      </div>

      <p className="mt-1 text-sm testimonials-role">{item.role}</p>
    </div>
  );
}
